import React, {
    FC,
    ReactNode,
    useMemo,
    useState,
    createContext,
    useContext,
} from 'react';
import { ZoomContainer } from '../../../css/styled/ZoomContainer';

interface ZoomContextProps {
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    resetZoom: () => void;
    incrementZoom: (incrementValue?: number) => void;
}

const ZoomContext = createContext<ZoomContextProps | undefined>(undefined);

interface ZoomContextProviderProps {
    children: ReactNode;
}

const ZoomContextProvider: FC<ZoomContextProviderProps> = ({ children }) => {
    const [zoom, setZoom] = useState<number>(100);

    const resetZoom = (): void => {
        setZoom(100);
    };

    const incrementZoom = (incrementValue: number = 10): void => {
        setZoom(zoom + incrementValue);
    };

    const memoizedData: ZoomContextProps = useMemo(
        () => ({
            resetZoom,
            incrementZoom,
            setZoom,
            zoom,
        }),
        [zoom],
    );

    return (
        <ZoomContext.Provider value={memoizedData}>
            <ZoomContainer $zoom={zoom}>{children}</ZoomContainer>
        </ZoomContext.Provider>
    );
};

export const useZoomContext = (): ZoomContextProps => {
    const context = useContext(ZoomContext) as ZoomContextProps;
    if (context === undefined)
        throw new Error(
            'useZoomContext must be used within a ZoomContextProvider',
        );
    return context;
};

export default ZoomContextProvider;
